import { useState, useEffect, useRef, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { FiPlus, FiEdit, FiTrash2, FiPackage, FiSearch, FiAlertCircle } from 'react-icons/fi';
import { toast } from 'react-toastify';
import inventoryService from '../services/inventoryService';
import Button from '../components/Button';
import './Technicians.css';
import '../assets/styles/ListTable.css';

const LIMIT_OPTIONS = [10, 20, 50, 100];

const categories = ['Spare Parts', 'Consumables', 'Refrigerant', 'Electrical', 'Filters', 'Compressor', 'Motors', 'Other'];

const Inventory = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [appliedSearch, setAppliedSearch] = useState('');
  const [filterCategory, setFilterCategory] = useState('all');
  const [showLowStock, setShowLowStock] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [pages, setPages] = useState(1);
  const [limitOption, setLimitOption] = useState('10');
  const [customLimit, setCustomLimit] = useState(25);
  const searchDebounce = useRef(null);

  const effectiveLimit = useMemo(() => {
    if (limitOption === 'custom') {
      const n = parseInt(customLimit, 10);
      return isNaN(n) ? 10 : Math.min(100, Math.max(1, n));
    }
    const n = parseInt(limitOption, 10);
    return isNaN(n) ? 10 : n;
  }, [limitOption, customLimit]);

  useEffect(() => {
    if (searchDebounce.current) clearTimeout(searchDebounce.current);
    searchDebounce.current = setTimeout(() => setAppliedSearch(searchTerm), 400);
    return () => { if (searchDebounce.current) clearTimeout(searchDebounce.current); };
  }, [searchTerm]);

  useEffect(() => {
    setCurrentPage(1);
  }, [appliedSearch, filterCategory, showLowStock, effectiveLimit]);

  useEffect(() => {
    fetchInventory();
  }, [currentPage, appliedSearch, filterCategory, showLowStock, effectiveLimit]);

  const fetchInventory = async () => {
    setLoading(true);
    try {
      const params = { page: currentPage, limit: effectiveLimit };
      if (appliedSearch) params.search = appliedSearch;
      if (filterCategory !== 'all') params.category = filterCategory;
      if (showLowStock) params.lowStock = 'true';
      const response = await inventoryService.getAll(params);
      setItems(response.data || []);
      setTotal(response.total ?? 0);
      setPages(response.pages ?? 1);
    } catch (error) {
      toast.error('Failed to fetch inventory');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this item?')) {
      try {
        await inventoryService.delete(id);
        toast.success('Item deleted successfully');
        fetchInventory();
      } catch (error) {
        toast.error('Failed to delete item');
      }
    }
  };

  const handleStockUpdate = async (id, operation) => {
    const quantity = prompt(`Enter quantity to ${operation}:`);
    if (quantity && !isNaN(quantity) && Number(quantity) > 0) {
      try {
        await inventoryService.updateStock(id, Number(quantity), operation);
        toast.success(`Stock ${operation}ed successfully`);
        fetchInventory();
      } catch (error) {
        toast.error(error.response?.data?.message || 'Failed to update stock');
      }
    }
  };

  const getStockStatus = (item) => {
    if (item.quantity === 0) return { text: 'Out of Stock', class: 'badge-error' };
    if (item.quantity <= item.minStockLevel) return { text: 'Low Stock', class: 'badge-warning' };
    return { text: 'In Stock', class: 'badge-success' };
  };

  if (loading) {
    return (
      <div className="loading-container">
        <div className="spinner"></div>
      </div>
    );
  }

  return (
    <div className="page-container">
      <div className="page-header">
        <div>
          <h2 className="page-title">Inventory</h2>
          <p className="page-subtitle">Manage your shop inventory</p>
        </div>
        <Link to="/inventory/new">
          <Button variant="primary" icon={<FiPlus />}>Add Item</Button>
        </Link>
      </div>

      <div className="page-filters">
        <div className="search-box">
          <FiSearch className="search-icon" />
          <input
            type="text"
            placeholder="Search by name or code..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="search-input"
          />
        </div>
        <select value={filterCategory} onChange={(e) => setFilterCategory(e.target.value)} className="filter-select">
          <option value="all">All Categories</option>
          {categories.map((cat) => (
            <option key={cat} value={cat}>{cat}</option>
          ))}
        </select>
        <label className="filter-checkbox">
          <input type="checkbox" checked={showLowStock} onChange={(e) => setShowLowStock(e.target.checked)} />
          <span>Low Stock Only</span>
        </label>
      </div>

      {items.length === 0 ? (
        <div className="empty-state">
          <FiPackage className="empty-icon" />
          <h3>No Items Found</h3>
          <p>Get started by adding your first inventory item</p>
          <Link to="/inventory/new">
            <Button variant="primary" icon={<FiPlus />}>Add Item</Button>
          </Link>
        </div>
      ) : (
        <>
          <div className="data-table-wrap">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Item</th>
                  <th>Code</th>
                  <th>Category</th>
                  <th>Quantity</th>
                  <th>Min Level</th>
                  <th>Price</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {items.map((item) => {
                  const stockStatus = getStockStatus(item);
                  return (
                    <tr key={item._id}>
                      <td><strong>{item.itemName}</strong></td>
                      <td>{item.itemCode}</td>
                      <td>{item.category}</td>
                      <td>{item.quantity} {item.unit}</td>
                      <td>{item.minStockLevel}</td>
                      <td>₹{item.price}</td>
                      <td>
                        <span className={`badge ${stockStatus.class}`}>{stockStatus.text}</span>
                        {item.quantity <= item.minStockLevel && <FiAlertCircle style={{ marginLeft: 4, color: 'var(--warning-500)' }} />}
                      </td>
                      <td>
                        <div className="cell-actions">
                          <button type="button" className="action-btn action-btn-view" onClick={() => handleStockUpdate(item._id, 'add')}>+ Add</button>
                          <button type="button" className="action-btn action-btn-view" onClick={() => handleStockUpdate(item._id, 'subtract')}>− Remove</button>
                          <Link to={`/inventory/${item._id}/edit`} className="action-btn action-btn-edit"><FiEdit /> Edit</Link>
                          <button type="button" className="action-btn action-btn-delete" onClick={() => handleDelete(item._id)}><FiTrash2 /></button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {(pages > 1 || total > items.length || total > 0) && (
            <div className="pagination-bar">
              <span className="pagination-info">
                Showing {(currentPage - 1) * effectiveLimit + 1}–{Math.min(currentPage * effectiveLimit, total)} of {total}
              </span>
              <div className="pagination-limit">
                <label className="pagination-limit-label">Per page</label>
                <select value={limitOption} onChange={(e) => setLimitOption(e.target.value)} className="pagination-limit-select">
                  {LIMIT_OPTIONS.map((n) => (<option key={n} value={String(n)}>{n}</option>))}
                  <option value="custom">Custom</option>
                </select>
                {limitOption === 'custom' && (
                  <input type="number" min={1} max={100} value={customLimit} onChange={(e) => setCustomLimit(parseInt(e.target.value, 10) || 10)} className="pagination-limit-input" />
                )}
              </div>
              <div className="pagination-controls">
                <button type="button" className="pagination-btn" onClick={() => setCurrentPage((p) => Math.max(1, p - 1))} disabled={currentPage === 1}>Previous</button>
                <span className="pagination-info" style={{ padding: '0 0.75rem' }}>Page {currentPage} of {pages}</span>
                <button type="button" className="pagination-btn" onClick={() => setCurrentPage((p) => Math.min(pages, p + 1))} disabled={currentPage === pages}>Next</button>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default Inventory;
